import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  FILTER_COAT,
  FILTER_COLOR,
  FILTER_GENDER,
  FILTER_HEALTH_TIER_LEVELS,
  FILTER_LOCATION_NAME,
  FILTER_POSTAL_CODE,
  FILTER_SIZE
} from 'dpl/constants/query_params';
import { isLessThanBreakpoint } from 'dpl/util/grid';

const IS_LT_MD = isLessThanBreakpoint('md');

export default function useStickyFilters({ container, tabsContainer } = {}) {
  const [stuck, setStuck] = useState(false);
  const ignoreNextChangeRef = useRef(false);

  function scrollToContainerParentTop() {
    const tabsHeight = tabsContainer?.clientHeight || 0;
    const marginOffset = (tabsHeight + (IS_LT_MD ? 2 : 10)) * -1;

    // Scroll below the tabs container to make sure that the exposed filters
    // remain stuck to the top of the page
    container.parentElement.style.scrollMarginTop = `${marginOffset}px`;
    container.parentElement.scrollIntoView();
  }

  const { locName, postalCode, size, color, coat, gender, healthTesting } =
    useSelector(
      ({ queryParams }) => ({
        locName: queryParams[FILTER_LOCATION_NAME],
        postalCode: queryParams[FILTER_POSTAL_CODE],
        size: queryParams[FILTER_SIZE],
        color: queryParams[FILTER_COLOR],
        coat: queryParams[FILTER_COAT],
        gender: queryParams[FILTER_GENDER],
        healthTesting: queryParams[FILTER_HEALTH_TIER_LEVELS]
      }),
      shallowEqual
    );

  useEffect(() => {
    // Ensure that exposed filters remain stuck after filtering
    // in cases where few or no results are rendered by scrolling
    // the window back to the top of the container's parent.
    if (stuck) scrollToContainerParentTop();
  }, [locName, postalCode, size, color, coat, gender, healthTesting]);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    if (!container) return undefined;

    const observer = new window.IntersectionObserver(
      ([e]) => {
        if (ignoreNextChangeRef.current) {
          ignoreNextChangeRef.current = false;
        } else {
          const shouldStick = e.intersectionRatio < 1;

          if (shouldStick !== stuck) {
            setStuck(shouldStick);

            // Ignore changes triggered by ExposedFilters component height change
            ignoreNextChangeRef.current = true;
          }
        }
      },
      { threshold: [1] }
    );

    observer.observe(container);

    return () => {
      observer.unobserve(container);
    };
  }, [container, stuck]);

  return { stuck };
}
